import api from './api'
import type { SupportStats, SupportConversation, ChatMessage, SupportUserProfile } from '../types'

// ── Dashboard / Stats ──────────────────────────────────────────────
export async function fetchAdminStats() {
  const res = await api.get('/admin/stats')
  return res.data?.data ?? res.data
}

// ── Users ──────────────────────────────────────────────────────────
export async function fetchUsers(params?: {
  search?: string
  role?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/users', { params })
  return res.data?.data ?? res.data
}

export async function toggleUserStatus(userId: string, isActive: boolean) {
  const res = await api.patch(`/admin/users/${userId}/status`, { isActive })
  return res.data?.data ?? res.data
}

export async function changeUserRole(userId: string, role: string) {
  const res = await api.patch(`/admin/users/${userId}/role`, { role })
  return res.data?.data ?? res.data
}

export async function deleteUser(userId: string) {
  const res = await api.delete(`/admin/users/${userId}`)
  return res.data?.data ?? res.data
}

export async function createUser(data: { name: string; email: string; phone?: string; password: string; role: string; address?: string; zones?: string[]; isSuperAdmin?: boolean }) {
  const res = await api.post('/admin/users', data)
  return res.data?.data ?? res.data
}

// ── Pickups ────────────────────────────────────────────────────────
export async function fetchPickups(params?: {
  status?: string
  wasteType?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/pickups', { params })
  return res.data?.data ?? res.data
}

export async function assignPickup(pickupId: string, collectorId: string) {
  const res = await api.patch(`/admin/pickups/${pickupId}/assign`, { collectorId })
  return res.data?.data ?? res.data
}

export async function autoAssignPickups() {
  const res = await api.post('/admin/pickups/auto-assign')
  return res.data?.data ?? res.data
}

export async function resolveDispute(pickupId: string, outcome: 'UPHELD' | 'REJECTED', note: string) {
  const res = await api.post(`/admin/pickups/${pickupId}/resolve-dispute`, { outcome, note })
  return res.data?.data ?? res.data
}

export async function cancelPickup(pickupId: string, reason?: string) {
  const res = await api.patch(`/admin/pickups/${pickupId}/cancel`, { reason })
  return res.data?.data ?? res.data
}

// ── Reports ────────────────────────────────────────────────────────
export async function fetchReports(params?: {
  status?: string
  category?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/reports', { params })
  return res.data?.data ?? res.data
}

export async function updateReportStatus(
  reportId: string,
  status: string,
  adminNotes?: string
) {
  const res = await api.patch(`/admin/reports/${reportId}/status`, {
    status,
    adminNotes,
  })
  return res.data?.data ?? res.data
}

// ── Collectors ─────────────────────────────────────────────────────
export async function fetchCollectors(params?: {
  status?: string
  companyId?: string
  search?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/collectors', { params })
  return res.data?.data ?? res.data
}

export async function fetchCollector(collectorId: string) {
  const res = await api.get(`/admin/collectors/${collectorId}`)
  return res.data?.data ?? res.data
}

export interface CreateCollectorPayload {
  name: string
  email: string
  phone?: string
  password: string
  address?: string
  zone?: string
  dateOfBirth?: string
  driverLicenseNumber?: string
  companyId?: string
  maxDailyPickups?: number
  isAvailable?: boolean
}

export async function createCollector(payload: CreateCollectorPayload) {
  const res = await api.post('/admin/collectors', payload)
  return res.data?.data ?? res.data
}

export async function updateCollector(
  collectorId: string,
  // companyId is nullable on update so a collector can be made independent again.
  payload: Partial<Omit<CreateCollectorPayload, 'password' | 'email' | 'companyId'>> & { companyId?: string | null },
) {
  const res = await api.patch(`/admin/collectors/${collectorId}`, payload)
  return res.data?.data ?? res.data
}

export async function approveCollector(collectorId: string) {
  const res = await api.patch(`/admin/collectors/${collectorId}/approve`)
  return res.data?.data ?? res.data
}

export async function rejectCollector(collectorId: string, reason?: string) {
  const res = await api.patch(`/admin/collectors/${collectorId}/reject`, { reason })
  return res.data?.data ?? res.data
}

export async function updateCollectorZone(collectorId: string, zone: string) {
  const res = await api.patch(`/admin/collectors/${collectorId}/zone`, { zone })
  return res.data?.data ?? res.data
}

// ── Admin Profile ───────────────────────────────────────────────────
export async function fetchAdminProfile() {
  const res = await api.get('/admin/me')
  return res.data?.data ?? res.data
}

// ── Map Data ───────────────────────────────────────────────────────
export async function fetchMapData(type?: string) {
  const res = await api.get('/admin/map-data', { params: type ? { type } : {} })
  return res.data?.data ?? res.data
}

// ── Admin Zone Assignment ──────────────────────────────────────────
export async function assignAdminZones(userId: string, zones: string[]) {
  const res = await api.patch(`/admin/users/${userId}/zones`, { zones })
  return res.data?.data ?? res.data
}

// ── Waste Distribution ───────────────────────────────────────────────
export async function fetchWasteDistribution() {
  const res = await api.get('/admin/waste-distribution')
  return res.data?.data ?? res.data
}

// ── Revenue Trend ───────────────────────────────────────────────────
export async function fetchRevenueTrend(days = 30) {
  const res = await api.get('/admin/revenue-trend', { params: { days } })
  return res.data?.data ?? res.data
}

// ── Payments ─────────────────────────────────────────────────────────
export async function fetchPayments(params?: {
  status?: string
  method?: string
  search?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/payments', { params })
  return res.data?.data ?? res.data
}

// ── Donations ──────────────────────────────────────────────────────────
export async function fetchDonations(params?: {
  status?: string
  search?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/donations', { params })
  return res.data?.data ?? res.data
}

export async function fetchDonationCampaignStats() {
  const res = await api.get('/donations/campaign')
  return res.data?.data ?? res.data
}

// ── Subscriptions ────────────────────────────────────────────────────
export async function fetchSubscriptions(params?: {
  active?: string
  plan?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/subscriptions', { params })
  return res.data?.data ?? res.data
}

// ── Ratings ─────────────────────────────────────────────────────────
export async function fetchRatings(params?: {
  page?: number
  limit?: number
  collectorId?: string
  minScore?: number
  maxScore?: number
}) {
  const res = await api.get('/admin/ratings', { params })
  return res.data?.data ?? res.data
}

// ── Notifications (admin broadcast) ────────────────────────────────
export interface SendNotificationResult {
  sent: number
  pushDelivered?: number
  emailQueued?: number
}

export async function sendNotification(payload: {
  title: string
  body: string
  type?: string
  userId?: string
  email?: boolean
}): Promise<SendNotificationResult> {
  const res = await api.post('/notifications/send', payload)
  return res.data?.data ?? res.data
}

// ── Payouts ──────────────────────────────────────────────────────────
export async function fetchPayoutRequests(params?: { status?: string; collectorId?: string; page?: number; limit?: number }) {
  const res = await api.get('/payouts/admin/requests', { params })
  return res.data?.data ?? res.data
}
export async function approvePayoutRequest(id: string) {
  const res = await api.patch(`/payouts/admin/requests/${id}/approve`)
  return res.data?.data ?? res.data
}
export async function rejectPayoutRequest(id: string, reason: string) {
  // An approved payout that went to Paystack is checked there before it can be
  // rejected, which can take up to 40 s. The default 15 s reported a reject
  // as failed that the server then completed.
  const res = await api.patch(`/payouts/admin/requests/${id}/reject`, { reason }, { timeout: 60000 })
  return res.data?.data ?? res.data
}
export async function processPayoutRequest(id: string) {
  // Paystack can take up to 20 s, then be asked again by reference: the
  // default 15 s hid the server's real answer behind a timeout.
  const res = await api.post(`/payouts/admin/requests/${id}/process`, undefined, { timeout: 60000 })
  return res.data?.data ?? res.data
}
export async function fetchPayRate() {
  const res = await api.get('/payouts/admin/settings/pay-rate')
  return res.data?.data ?? res.data
}
export async function updatePayRate(payRate: number) {
  const res = await api.patch('/payouts/admin/settings/pay-rate', { payRate })
  return res.data?.data ?? res.data
}

// ── Support / Chat ──────────────────────────────────────────────────
export async function fetchSupportStats(): Promise<SupportStats> {
  const res = await api.get('/chat/support/stats')
  return res.data?.data ?? res.data
}

export async function fetchSupportConversations(params?: {
  status?: string; category?: string; search?: string; page?: number; limit?: number
}): Promise<{ conversations: SupportConversation[]; total?: number; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
  const res = await api.get('/chat/support', { params })
  return res.data?.data ?? res.data
}

export async function fetchSupportConversation(id: string, page = 1): Promise<{
  conversation: SupportConversation; messages: ChatMessage[]; total: number; userProfile?: SupportUserProfile
}> {
  const res = await api.get(`/chat/support/${id}?page=${page}&limit=50`)
  return res.data?.data ?? res.data
}

export async function sendSupportReply(id: string, content: string): Promise<ChatMessage> {
  const res = await api.post(`/chat/support/${id}/message`, { content })
  return res.data?.data ?? res.data
}

export async function updateSupportStatus(id: string, status: string): Promise<SupportConversation> {
  const res = await api.patch(`/chat/support/${id}/status`, { status })
  return res.data?.data ?? res.data
}

export async function markSupportRead(id: string): Promise<void> {
  await api.patch(`/chat/support/${id}/read`)
}

export async function fetchPickupChat(pickupId: string, page = 1): Promise<{ messages: ChatMessage[]; total: number }> {
  const res = await api.get(`/chat/pickup/${pickupId}?page=${page}&limit=50`)
  return res.data?.data ?? res.data
}

// ── Refunds ───────────────────────────────────────────────────────────
export async function refundPayment(
  paymentId: string,
  body: { destination: 'CREDIT' | 'GATEWAY'; reason?: string },
) {
  // /payments/:id/refund is the route that calls Paystack (admin-only inside
  // the handler). The old /admin/payments/:id/refund only flipped the row to
  // REFUNDED and told the resident their money was returned — it never issued
  // a refund. That route is gone; this is the only path. Where the money goes
  // is the admin's choice and the backend insists on it: CREDIT lands on the
  // resident's SmartWasteCycle balance at once, GATEWAY goes back through
  // Paystack to the wallet or card that paid.
  const res = await api.post(`/payments/${paymentId}/refund`, body)
  return res.data?.data ?? res.data
}

/**
 * Put a gateway refund that Paystack failed or never settled onto the
 * resident's SmartWasteCycle credit instead. The backend tells the resident
 * and records REFUND_TO_CREDIT; it answers 409 once the refund is no longer
 * waiting on Paystack (already moved, or the webhook settled it).
 */
export async function moveRefundToCredit(paymentId: string, body: { reason?: string }) {
  const res = await api.post(`/payments/${paymentId}/refund/to-credit`, body)
  return res.data?.data ?? res.data
}

// ── Subscription Actions ──────────────────────────────────────────────
export async function updateSubscription(
  subId: string,
  action: 'extend' | 'cancel',
  days?: number
) {
  const res = await api.patch(`/admin/subscriptions/${subId}`, { action, days })
  return res.data?.data ?? res.data
}

// ── Admin Pickups ─────────────────────────────────────────────────────

// ── Audit Logs ────────────────────────────────────────────────────────
export async function fetchAuditLogs(params?: {
  action?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/audit-logs', { params })
  return res.data?.data ?? res.data
}

// ── Companies ──────────────────────────────────────────────────────
export interface CompanyPayload {
  name: string
  registrationNumber?: string
  contactName: string
  contactEmail: string
  contactPhone?: string
  address?: string
  serviceZones?: string[]
  notes?: string
}

export async function fetchCompanies(params?: {
  status?: string
  search?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/companies', { params })
  return res.data?.data ?? res.data
}

export async function fetchCompany(companyId: string) {
  const res = await api.get(`/admin/companies/${companyId}`)
  return res.data?.data ?? res.data
}

export async function createCompany(payload: CompanyPayload) {
  const res = await api.post('/admin/companies', payload)
  return res.data?.data ?? res.data
}

export async function updateCompany(companyId: string, payload: Partial<CompanyPayload>) {
  const res = await api.patch(`/admin/companies/${companyId}`, payload)
  return res.data?.data ?? res.data
}

export async function setCompanyStatus(companyId: string, status: 'ACTIVE' | 'SUSPENDED', reason?: string) {
  const res = await api.patch(`/admin/companies/${companyId}/status`, { status, reason })
  return res.data?.data ?? res.data
}

export async function deleteCompany(companyId: string) {
  const res = await api.delete(`/admin/companies/${companyId}`)
  return res.data?.data ?? res.data
}

export async function attachCollectorsToCompany(companyId: string, collectorIds: string[]) {
  const res = await api.post(`/admin/companies/${companyId}/collectors`, { collectorIds })
  return res.data?.data ?? res.data
}

export async function detachCollectorFromCompany(companyId: string, collectorId: string) {
  const res = await api.delete(`/admin/companies/${companyId}/collectors/${collectorId}`)
  return res.data?.data ?? res.data
}

// ── Resident funnel ────────────────────────────────────────────────
/** How many residents got how far in the last N days (7, 30 or 90). */
export async function fetchAdminFunnel(days: 7 | 30 | 90 = 30) {
  const res = await api.get('/admin/funnel', { params: { days } })
  return res.data?.data
}

// ── Pickup assignment offers ───────────────────────────────────────
export async function fetchAssignments(params?: {
  status?: string
  collectorId?: string
  page?: number
  limit?: number
}) {
  const res = await api.get('/admin/assignments', { params })
  return res.data?.data ?? res.data
}

export async function fetchPickupAssignments(pickupId: string) {
  const res = await api.get(`/admin/pickups/${pickupId}/assignments`)
  return res.data?.data ?? res.data
}

export async function offerPickup(pickupId: string, collectorId?: string) {
  const res = await api.post(`/admin/pickups/${pickupId}/offer`, collectorId ? { collectorId } : {})
  return res.data?.data ?? res.data
}

/**
 * Pickups the offer engine gave up on, oldest first, each with the nearest
 * collectors who have not already been asked.
 */
export async function fetchEscalatedPickups(limit = 20) {
  const res = await api.get('/admin/pickups/escalated', { params: { limit } })
  return res.data?.data
}

export async function revokePickupOffer(pickupId: string, reoffer = false) {
  const res = await api.post(`/admin/pickups/${pickupId}/revoke-offer`, { reoffer })
  return res.data?.data ?? res.data
}

// ── Zones ──────────────────────────────────────────────────────────
export interface ZoneSummary {
  zone: string
  collectors: number
  pickups: number
  companies: number
  sampleAddress: string | null
}

export async function fetchZones(): Promise<{ zones: ZoneSummary[]; total: number }> {
  const res = await api.get('/admin/zones')
  return res.data?.data ?? res.data
}

// ── Company logins and money ───────────────────────────────────────
export async function fetchCompanyAdmins(companyId: string) {
  const res = await api.get(`/admin/companies/${companyId}/admins`)
  return res.data?.data ?? res.data
}

export async function createCompanyAdmin(
  companyId: string,
  payload: { name: string; email: string; phone?: string; password: string },
) {
  const res = await api.post(`/admin/companies/${companyId}/admins`, payload)
  return res.data?.data ?? res.data
}

/**
 * Suspend or restore one of an operator's logins.
 *
 * The backend refuses suspending the last active one, so the operator is never
 * locked out of their own portal by a single click here.
 */
export async function setCompanyAdminActive(
  companyId: string,
  userId: string,
  isActive: boolean,
) {
  const res = await api.patch(`/admin/companies/${companyId}/admins/${userId}`, { isActive })
  return res.data?.data ?? res.data
}

/** Issue a new password. The holder must choose their own before they can work. */
export async function resetCompanyAdminPassword(
  companyId: string,
  userId: string,
  password: string,
) {
  const res = await api.post(`/admin/companies/${companyId}/admins/${userId}/password`, { password })
  return res.data?.data ?? res.data
}

/** Remove a login's access for good: deactivated and detached from the company. */
export async function revokeCompanyAdmin(companyId: string, userId: string) {
  const res = await api.delete(`/admin/companies/${companyId}/admins/${userId}`)
  return res.data?.data ?? res.data
}

export async function fetchCompanyEarnings(companyId: string) {
  const res = await api.get(`/admin/companies/${companyId}/earnings`)
  return res.data?.data ?? res.data
}

// ── Company payouts (admin review) ─────────────────────────────────
export async function fetchCompanyPayoutRequests(params?: { status?: string; page?: number; limit?: number }) {
  const res = await api.get('/admin/company-payouts', { params })
  return res.data?.data ?? res.data
}

export async function approveCompanyPayout(id: string) {
  const res = await api.patch(`/admin/company-payouts/${id}/approve`)
  return res.data?.data ?? res.data
}

export async function rejectCompanyPayout(id: string, reason: string) {
  // Checked with Paystack first, as on the collector rail: up to 40 s.
  const res = await api.patch(`/admin/company-payouts/${id}/reject`, { reason }, { timeout: 60000 })
  return res.data?.data ?? res.data
}

export async function processCompanyPayout(id: string) {
  const res = await api.post(`/admin/company-payouts/${id}/process`, undefined, { timeout: 60000 })
  return res.data?.data ?? res.data
}

export async function fetchCompanyBalance(companyId: string) {
  const res = await api.get(`/admin/companies/${companyId}/balance`)
  return res.data?.data ?? res.data
}

// ── System Health / Observability ──────────────────────────────────
//
// Reads Cloud Error Reporting, Cloud Logging and Cloud Monitoring back through
// the backend, which holds the credentials. Super Admin only; see
// backend/src/routes/adminObservability.ts.

export type ErrorPeriod =
  | 'PERIOD_1_HOUR'
  | 'PERIOD_6_HOURS'
  | 'PERIOD_1_DAY'
  | 'PERIOD_1_WEEK'
  | 'PERIOD_30_DAYS'

export interface ErrorGroup {
  groupId: string
  count: number
  affectedUsers: number
  firstSeen?: string
  lastSeen?: string
  service?: string
  version?: string
  summary: string
  stack?: string
  local: { firstSeenAt: string; lastNotifiedAt: string | null; resolvedAt: string | null } | null
}

export interface HealthSummary {
  database: string
  revision: string
  region: string
  observabilityAvailable: boolean
  newGroups24h: number
  unresolvedGroups: number
  consoleUrl: string
}

export interface TimeSeriesResult {
  label: string
  points: { time: string; value: number }[]
}

export interface PerformanceSnapshot {
  latency: { p50: TimeSeriesResult[]; p95: TimeSeriesResult[]; p99: TimeSeriesResult[] }
  requestsByClass: TimeSeriesResult[]
  cpu: TimeSeriesResult[]
  memory: TimeSeriesResult[]
  instances: TimeSeriesResult[]
}

export async function fetchHealthSummary(): Promise<HealthSummary> {
  const res = await api.get('/admin/observability/summary')
  return res.data?.data ?? res.data
}

export async function fetchErrorGroups(params?: {
  period?: ErrorPeriod
  service?: string
  pageSize?: number
}): Promise<ErrorGroup[]> {
  const res = await api.get('/admin/observability/errors', { params })
  return res.data?.data ?? res.data
}

export async function fetchErrorEvents(groupId: string, period?: ErrorPeriod) {
  const res = await api.get(`/admin/observability/errors/${encodeURIComponent(groupId)}/events`, {
    params: { period },
  })
  return res.data?.data ?? res.data
}

export async function resolveErrorGroup(groupId: string, resolved: boolean) {
  const res = await api.patch(
    `/admin/observability/errors/${encodeURIComponent(groupId)}/resolve`,
    { resolved }
  )
  return res.data?.data ?? res.data
}

export async function fetchPerformanceMetrics(minutes = 360): Promise<PerformanceSnapshot> {
  const res = await api.get('/admin/observability/metrics', { params: { minutes } })
  return res.data?.data ?? res.data
}
